import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import moment from 'moment-timezone';
import { CashReportDetailFilters, exportCashReportDetailThunk } from '@spike/payments-action';
import { CashReportDetailRow } from '@spike/payments-model';
import { useNonInitialEffect } from '@versiondos/hooks';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { RootState } from 'store';
import { categoryLabel, paymentTypeLabel } from './cashReportDetailLabels';

interface CashReportDetailExportCSVProps {
    filters: CashReportDetailFilters;
}

const PURCHASE_HEADERS = ['Date', 'Time', 'Client', 'Item', 'Quantity', 'Invoice', 'Payment Type', 'Amount'];
const REFUND_HEADERS = ['Date', 'Time', 'Client', 'Invoice', 'Payment Type', 'Amount'];

// ExportCSV writes without enclosing characters and with ';' as separator, so these would break the columns.
const cleanCell = (value: string | null | undefined) => (value ?? '').replace(/[;\r\n]+/g, ' ').trim();

const CashReportDetailExportCSV: React.FC<CashReportDetailExportCSVProps> = ({ filters }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const csvRef = useRef<any>(null);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<string[][]>([]);

    const isRefund = filters.category === 'refund';

    useNonInitialEffect(() => {
        if (records.length > 0) {
            csvRef.current?.export();
        }
    }, [records]);

    const toRecord = (row: CashReportDetailRow) => {
        const common = [row.occurredAt.format('YYYY-MM-DD'), row.occurredAt.format('h:mm A'), row.customerName];
        const invoice = row.invoiceNumber != null ? String(row.invoiceNumber) : '';
        const payment = [invoice, paymentTypeLabel(row.paymentType), row.amount.toFixed(2)];

        return (
            isRefund ? [...common, ...payment] : [...common, row.itemName, String(row.quantity ?? ''), ...payment]
        ).map(cleanCell);
    };

    const exportHandler = async () => {
        setLoading(true);
        try {
            const result = await dispatch(exportCashReportDetailThunk(apiClientWrapper, filters));
            setRecords(result.rows.map(toRecord));
        } catch (error) {
            // the thunk already shows the error alert
        } finally {
            setLoading(false);
        }
    };

    const fileName = `Cash_Report_${categoryLabel(filters.category).replace(
        /[\s-]+/g,
        ''
    )}_${marketplace.basics.businessName.replace(/\s+/g, '')}_${moment().format('YYYYMMDD_HHmm')}.csv`;

    return (
        <ExportCSV
            ref={csvRef}
            headers={isRefund ? REFUND_HEADERS : PURCHASE_HEADERS}
            records={records}
            filename={fileName}
            exporting={loading}
            onClick={exportHandler}
        />
    );
};

export default CashReportDetailExportCSV;
